/**
 * The bottom row of a `WorkshopList` card: the pacing flag, the sales fill
 * bar, and the sold/cap count. The bar and the count are independently
 * switchable.
 *
 * The pacing flag renders the status the server sends, through the existing
 * `StatusPill`. The client never derives that status from sold and capacity —
 * the server already owns "how full is full", and two implementations of it
 * is how they drift. Sold-out is a separate, local question: at or over
 * capacity the bar and count switch to a warning colour, so a full house is
 * legible without reading the numbers. That is the design's own behaviour.
 *
 * Sits directly under the content, not pinned to the card's bottom — a card
 * with no staff line used to leave a band of empty space above a
 * `marginTop: auto` fill; mild on a phone, conspicuous at tablet width.
 *
 * The bar is drawn here rather than through the shared `FillBar` primitive,
 * whose geometry is a different shape from the one the design draws on this
 * row. It carries the occupancy as a progress bar because the count beside
 * it is switchable: with `showCount` off, the bar is the only thing on the
 * row saying how full the workshop is.
 */
import { StatusPill } from './StatusPill';
import type { StatusTone } from './StatusPill';
import type { WorkshopListSize } from './workshopListSizes';
import { WORKSHOP_LIST_SIZES } from './workshopListSizes';

/** How full the bar draws. Guarded rather than trusting the caller's switch:
 *  a bar forced on for a workshop with no capacity would divide by zero and
 *  write `NaN%` straight into the style attribute, where it fails silently. */
function fillPercent(booked: number, capacity: number): number {
  if (capacity <= 0) return 0;
  return Math.min(100, Math.round((booked / capacity) * 100));
}

export interface WorkshopListSalesRowProps {
  /** Whether this row renders at all — off when nothing in it has anything
   *  to show, or when a custom `right` renderer replaces it. */
  show: boolean;
  booked: number;
  capacity: number;
  showBar: boolean;
  showCount: boolean;
  status?: StatusTone;
  showPacing: boolean;
  size?: WorkshopListSize;
}

export function WorkshopListSalesRow({
  show,
  booked,
  capacity,
  showBar,
  showCount,
  status,
  showPacing,
  size = 'md',
}: WorkshopListSalesRowProps) {
  if (!show) return null;
  const soldOut = capacity > 0 && booked >= capacity;
  const tone = soldOut ? 'var(--nb-warn)' : 'var(--nb-primary)';
  const z = WORKSHOP_LIST_SIZES[size];
  return (
    <div
      data-testid="workshop-list-sales-row"
      style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}
    >
      {showPacing && status && <StatusPill tone={status} fontSize={z.count - 1} />}
      {showBar && (
        <div
          data-testid="workshop-list-sales-track"
          role="progressbar"
          aria-valuenow={booked}
          aria-valuemin={0}
          aria-valuemax={capacity}
          aria-label={`${booked} of ${capacity} seats`}
          style={{
            flex: 1,
            height: 5,
            background: 'var(--nb-surface-2)',
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          <div
            data-testid="workshop-list-sales-fill"
            style={{
              width: `${fillPercent(booked, capacity)}%`,
              height: '100%',
              borderRadius: 3,
              background: tone,
            }}
          />
        </div>
      )}
      {showCount && (
        <span
          className="nb-tabular nb-mono"
          style={{
            flexShrink: 0,
            marginLeft: showBar ? 0 : 'auto',
            fontSize: z.count,
            fontWeight: 700,
            color: tone,
          }}
        >
          {booked}/{capacity}
        </span>
      )}
    </div>
  );
}
